import { RPCs } from "../../../services/rpc.js";
import { ALL_USERS_ID } from "../constants.js";
import { SelectMultiOptionWrapper } from "../../../components/Select/SelectMultiOptionWrapper.js";
import { SelectAsyncRPC } from "../../../components/Select/SelectAsyncRPC.js";
import { UserBadgeWide } from "./UserBadgeWide.js";
import { UserGeoRelativeLocationLabel } from "./UserGeoRelativeLocationLabel.js";
import { UserAnPCheck } from "../../shared/UserAnPCheck.js";
import { useRegionContext } from "../../Regions/contexts/RegionContext.js";
import { useMemo } from "react";
import { Icon, Tag } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import * as turf from "@turf/turf";
import { MdGpsFixed, MdLocationPin } from "react-icons/md";
//#region src/modules/Users/shared/SelectUser.tsx
var ADDITIONAL_OPTIONS = [{ id: ALL_USERS_ID }];
var Base = ({ showDistanceFromGeo, showStatus, user }) => {
	if (!user) return null;
	if (user.id === "usr-") return /* @__PURE__ */ jsx("div", { children: "All Users" });
	const { position, status } = user;
	const distanceFrom = useMemo(() => {
		if (showDistanceFromGeo?.lng && showDistanceFromGeo?.lat) return turf.point([showDistanceFromGeo.lng, showDistanceFromGeo.lat]);
	}, [showDistanceFromGeo]);
	const distanceTo = useMemo(() => {
		if (user.lastGeolocation?.coords) return turf.point([user.lastGeolocation?.coords.lng, user.lastGeolocation?.coords.lat]);
	}, [user]);
	const calculateDistance = useMemo(() => {
		if (!!distanceFrom?.geometry && distanceTo) return turf.distance(distanceFrom, distanceTo, { units: "miles" });
	}, [distanceFrom, distanceTo]);
	return /* @__PURE__ */ jsxs("div", {
		className: "space-x-xxs gap-xxs flex w-full flex-wrap-reverse items-end justify-end",
		children: [/* @__PURE__ */ jsx("div", {
			className: "grow",
			children: /* @__PURE__ */ jsx(UserBadgeWide, {
				user,
				size: "sm"
			})
		}), showStatus && /* @__PURE__ */ jsxs("div", {
			className: "gap-xxs flex flex-wrap justify-end text-right text-xs",
			children: [
				!!calculateDistance && !!user.status && /* @__PURE__ */ jsx(Tag, {
					variant: "info",
					value: parseFloat(calculateDistance.toFixed(1)) === 0 ? /* @__PURE__ */ jsx("div", {
						className: "text-xs",
						children: "On Site"
					}) : /* @__PURE__ */ jsxs("div", {
						className: "text-xs",
						children: [
							/* @__PURE__ */ jsx("span", { children: parseFloat(calculateDistance.toFixed(1)) }),
							" ",
							/* @__PURE__ */ jsx("span", {
								className: "truncate",
								children: "miles away"
							})
						]
					})
				}),
				!!status && /* @__PURE__ */ jsxs("div", {
					className: "",
					children: [status?.title ? /* @__PURE__ */ jsx("span", {
						className: "",
						style: { color: status?.color },
						children: status?.title
					}) : /* @__PURE__ */ jsx("span", {
						className: "italics text-color-subtle opacity-50",
						children: "No Status"
					}), status?.geoTracking && /* @__PURE__ */ jsx(Icon, {
						className: "pl-xxs opacity-60",
						Icon: MdGpsFixed,
						color: "subtle",
						size: "xs"
					})]
				}),
				!!status && /* @__PURE__ */ jsx("div", { children: position?.title ? /* @__PURE__ */ jsx("span", {
					className: "truncate",
					style: { color: position?.color },
					children: position?.title
				}) : /* @__PURE__ */ jsx("span", {
					className: "italics text-color-subtle opacity-50",
					children: "No Position"
				}) }),
				/* @__PURE__ */ jsx(UserAnPCheck, {
					check: ({ anp }) => anp.role.ops.access.viewOnDutyUsersGeoLocation,
					children: !!status && user.lastGeolocation?.relativeLocation && /* @__PURE__ */ jsxs("span", {
						className: "font-medium",
						children: [/* @__PURE__ */ jsx(Icon, {
							className: "opacity-60",
							Icon: MdLocationPin,
							color: "subtle",
							size: "xs"
						}), /* @__PURE__ */ jsx(UserGeoRelativeLocationLabel, { relativeLocation: user.lastGeolocation.relativeLocation })]
					})
				})
			]
		})]
	});
};
var SelectUserRenderComponents = {
	ListItem: ({ showDistanceFromGeo, showStatus, userOption }) => /* @__PURE__ */ jsx(Base, {
		showStatus,
		user: userOption,
		showDistanceFromGeo
	}),
	SelectedMulti: (user, onRemove) => /* @__PURE__ */ jsx(SelectMultiOptionWrapper, {
		onRemove,
		children: /* @__PURE__ */ jsx("div", {
			className: "px-sm max-w-full",
			children: /* @__PURE__ */ jsx(Base, { user })
		})
	}),
	SelectedSingle: (user) => /* @__PURE__ */ jsx(Base, { user })
};
var responseOptionsExtractor = (data) => data?.users;
var SelectUser = ({ active = true, allowAllOption, hasStatus, idFilter, limit = 25, multi, rolesFilter, showDistanceFromGeo, showStatus, usersWithStatusFirst, value, ...atalayaSelectProps }) => {
	const regionCtx = useRegionContext();
	return /* @__PURE__ */ jsx(SelectAsyncRPC, {
		additionalOptions: allowAllOption ? ADDITIONAL_OPTIONS : void 0,
		RenderListItem: (list) => /* @__PURE__ */ jsx(SelectUserRenderComponents.ListItem, {
			userOption: list,
			showStatus,
			showDistanceFromGeo
		}),
		RenderSelectedListItem: (list) => /* @__PURE__ */ jsx(SelectUserRenderComponents.ListItem, {
			userOption: list,
			showStatus,
			showDistanceFromGeo
		}),
		RenderSelectedMulti: SelectUserRenderComponents.SelectedMulti,
		RenderSelectedSingle: SelectUserRenderComponents.SelectedSingle,
		args: {
			active,
			hasStatus,
			idFilter,
			regionIds: regionCtx ? [regionCtx?.regionId] : void 0,
			rolesFilter,
			usersWithStatusFirst
		},
		idProp: "id",
		limit,
		multi,
		optionLabelExtractor: ({ displayName }) => displayName,
		optionValueExtractor: ({ id }) => id,
		request: RPCs.Users.GetUsersList.v1,
		requestFilterProp: "idFilter",
		responseOptionsExtractor,
		value,
		...atalayaSelectProps
	});
};
//#endregion
export { SelectUser, SelectUserRenderComponents };

//# sourceMappingURL=SelectUser.js.map